import { useState, useCallback, useMemo } from "react";
import {
  Button,
  Group,
  Stack,
  Text,
  Card,
  Badge,
  Loader,
  Center,
  Box,
  Divider,
  Modal,
  Select,
} from "@mantine/core";
import { IconAlertTriangle, IconPlus, IconCheck } from "@tabler/icons-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { notifications } from "@mantine/notifications";
import { useNavigate } from "react-router-dom";
import { api } from "../../lib";
import ErrorReportModal from "../grossing/ErrorReportModal";
import RichTextDiagnosis from "./RichTextDiagnosis";
import { usePendingCorrectionsForCase } from "../../hooks/useCorrectionRequests";
import { sanitizeHtml } from "../../utils/sanitize";
import { errorRefLabel, correctionRefLabel } from "../../utils/formatters";

interface CaseError {
  id: string;
  refNumber?: number;
  discrepancy?: {
    error: string;
    description: string;
    category: string;
  };
  errorType?: {
    error: string;
    description: string;
    category: string;
  };
  description: string;
  blockCase: boolean;
  preventSignOff: boolean;
  status: "pending" | "resolved";
  resolvedAt?: string;
  resolvedBy?: {
    fullName: string;
  };
  resolutionNotes?: string;
  correctionRequestId?: string | null;
  correctionRequest?: { id?: string; refNumber?: number };
  reportedAt: string;
  reportedBy?: {
    fullName: string;
  };
  createdAt?: string;
  createdBy?: {
    fullName: string;
  };
}

interface ErrorsCenterProps {
  caseId: string | null;
}

const ERROR_DISPLAY_NAMES: Record<string, string> = {
  SpecNotRcvd: "Specimen Not Received",
  PatData: "Patient Data Missing",
  NoSpec: "No Specimen in Container",
};

function getErrorDisplayName(errorCode: string): string {
  return ERROR_DISPLAY_NAMES[errorCode] || errorCode;
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatDateTime(dateStr: string): string {
  return new Date(dateStr).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export function ErrorsCenter({ caseId }: ErrorsCenterProps) {
  const [errorModalOpened, setErrorModalOpened] = useState(false);
  const [selectedError, setSelectedError] = useState<CaseError | null>(null);
  const [detailModalOpened, setDetailModalOpened] = useState(false);
  const [resolutionNotes, setResolutionNotes] = useState("");
  const [linkedCorrectionId, setLinkedCorrectionId] = useState<string | null>(
    null
  );
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { data: errors = [], isLoading } = useQuery({
    queryKey: ["caseErrors", caseId],
    queryFn: async () => {
      if (!caseId) return [];
      return api.get<CaseError[]>(`/workflows/errors/case/${caseId}`);
    },
    enabled: !!caseId,
  });

  const resolveMutation = useMutation({
    mutationFn: async ({
      errorId,
      resolutionNotes: notes,
      correctionRequestId,
    }: {
      errorId: string;
      resolutionNotes: string;
      correctionRequestId: string | null;
    }) => {
      return api.post(`/workflows/errors/${errorId}/resolve`, {
        resolutionNotes: notes,
        correctionRequestId,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["caseErrors", caseId] });
      queryClient.invalidateQueries({
        queryKey: ["case-for-pathologist-review", caseId],
      });
      queryClient.invalidateQueries({ queryKey: ["case", caseId] });
      queryClient.invalidateQueries({ queryKey: ["pathologist-review-queue"] });
      setDetailModalOpened(false);
      setSelectedError(null);
      setResolutionNotes("");
      setLinkedCorrectionId(null);
      notifications.show({
        title: "Error Resolved",
        message: "The error has been marked as resolved",
        color: "green",
      });
    },
    onError: () => {
      notifications.show({
        title: "Error",
        message: "Failed to resolve error",
        color: "red",
      });
    },
  });

  const { data: pendingCorrectionsData } = usePendingCorrectionsForCase(
    caseId ?? undefined,
    detailModalOpened && selectedError?.status === "pending"
  );

  const correctionOptions = useMemo(
    () =>
      (pendingCorrectionsData?.correctionRequests ?? []).map((cr) => ({
        value: cr.id,
        label: `${
          correctionRefLabel(cr.refNumber)
            ? `${correctionRefLabel(cr.refNumber)} — `
            : ""
        }${cr.requestor.name ?? "Unknown"} · ${
          Object.keys(cr.proposedChanges ?? {}).length
        } field change(s) · ${new Date(cr.requestedAt).toLocaleDateString()}`,
      })),
    [pendingCorrectionsData]
  );

  const unresolvedErrors = useMemo(
    () => errors.filter((e) => e.status === "pending"),
    [errors]
  );

  const resolvedErrors = useMemo(
    () => errors.filter((e) => e.status === "resolved"),
    [errors]
  );

  const handleCloseDetailModal = useCallback(() => {
    setDetailModalOpened(false);
    setSelectedError(null);
    setResolutionNotes("");
    setLinkedCorrectionId(null);
  }, []);

  const handleErrorClick = useCallback((error: CaseError) => {
    setSelectedError(error);
    setResolutionNotes("");
    setLinkedCorrectionId(null);
    setDetailModalOpened(true);
  }, []);

  const handleResolve = useCallback(() => {
    if (selectedError) {
      resolveMutation.mutate({
        errorId: selectedError.id,
        resolutionNotes,
        correctionRequestId: linkedCorrectionId,
      });
    }
  }, [selectedError, resolveMutation, resolutionNotes, linkedCorrectionId]);

  const handleOpenErrorModal = useCallback(() => {
    setErrorModalOpened(true);
  }, []);

  const handleCloseErrorModal = useCallback(() => {
    setErrorModalOpened(false);
  }, []);

  const handleNavigateToCorrections = useCallback(() => {
    navigate(`/cases/${caseId}?tab=corrections`);
  }, [navigate, caseId]);

  if (!caseId) {
    return (
      <Box>
        <Text c="dimmed" ta="center" size="sm" py="xl">
          Save the case first to report errors
        </Text>
      </Box>
    );
  }

  return (
    <>
      <Stack gap="lg">
        <Group justify="space-between" align="center">
          <Group gap="xs">
            <IconAlertTriangle
              size={20}
              color={
                unresolvedErrors.length > 0
                  ? "var(--mantine-color-orange-6)"
                  : "var(--mantine-color-gray-6)"
              }
            />
            <Text size="lg" fw={600}>
              Errors
            </Text>
            {unresolvedErrors.length > 0 && (
              <Badge size="sm" variant="filled" color="orange">
                {unresolvedErrors.length} pending
              </Badge>
            )}
            {unresolvedErrors.length === 0 && resolvedErrors.length > 0 && (
              <Badge size="sm" variant="light" color="green">
                all resolved
              </Badge>
            )}
          </Group>
          <Button
            size="sm"
            variant="outline"
            color="orange"
            leftSection={<IconPlus size={14} />}
            onClick={handleOpenErrorModal}
          >
            Report Error
          </Button>
        </Group>

        <Divider />

        {isLoading ? (
          <Center py="xl">
            <Loader size="sm" />
          </Center>
        ) : errors.length === 0 ? (
          <Center py="xl">
            <Stack align="center" gap="xs">
              <IconAlertTriangle
                size={40}
                color="var(--mantine-color-gray-4)"
              />
              <Text c="dimmed" size="sm">
                No errors reported for this case.
              </Text>
            </Stack>
          </Center>
        ) : (
          <Stack gap="md">
            {unresolvedErrors.length > 0 && (
              <Stack gap="sm">
                <Text size="sm" fw={600} c="orange">
                  Pending ({unresolvedErrors.length})
                </Text>
                {unresolvedErrors.map((error) => (
                  <Card
                    key={error.id}
                    withBorder
                    p="md"
                    shadow="xs"
                    bg="orange.0"
                    style={{ cursor: "pointer" }}
                    onClick={() => handleErrorClick(error)}
                  >
                    <Stack gap="xs">
                      <Group justify="space-between" wrap="nowrap">
                        <Group gap="xs">
                          {errorRefLabel(error.refNumber) && (
                            <Badge size="sm" variant="outline" color="blue">
                              {errorRefLabel(error.refNumber)}
                            </Badge>
                          )}
                          <Badge size="sm" color="orange" variant="filled">
                            PENDING
                          </Badge>
                          {error.blockCase && (
                            <Badge size="sm" color="red" variant="filled">
                              Blocks Case
                            </Badge>
                          )}
                          {error.preventSignOff && (
                            <Badge size="sm" color="orange" variant="light">
                              Prevents Sign-Off
                            </Badge>
                          )}
                        </Group>
                      </Group>
                      <Text size="sm" fw={500}>
                        {error.discrepancy?.error || error.errorType?.error
                          ? getErrorDisplayName(
                              error.discrepancy?.error ||
                                error.errorType?.error ||
                                ""
                            )
                          : error.description || "Unknown Error"}
                      </Text>
                      <div
                        className="rich-text-display"
                        style={{
                          fontSize: "var(--mantine-font-size-sm)",
                          color: "var(--mantine-color-dimmed)",
                          wordBreak: "break-word",
                        }}
                        // eslint-disable-next-line react/no-danger
                        dangerouslySetInnerHTML={{
                          __html: sanitizeHtml(error.description || ""),
                        }}
                      />
                      <Text size="xs" c="dimmed">
                        Reported by{" "}
                        {error.reportedBy?.fullName ||
                          error.createdBy?.fullName ||
                          "Unknown"}{" "}
                        •{" "}
                        {formatDate(error.reportedAt || error.createdAt || "")}
                      </Text>
                    </Stack>
                  </Card>
                ))}
              </Stack>
            )}

            {resolvedErrors.length > 0 && (
              <Stack gap="sm">
                {unresolvedErrors.length > 0 && <Divider />}
                <Text size="sm" fw={600} c="green">
                  Resolved ({resolvedErrors.length})
                </Text>
                {resolvedErrors.map((error) => (
                  <Card
                    key={error.id}
                    withBorder
                    p="md"
                    shadow="xs"
                    style={{ cursor: "pointer", opacity: 0.8 }}
                    onClick={() => handleErrorClick(error)}
                  >
                    <Stack gap="xs">
                      <Group gap="xs">
                        {errorRefLabel(error.refNumber) && (
                          <Badge size="sm" variant="outline" color="blue">
                            {errorRefLabel(error.refNumber)}
                          </Badge>
                        )}
                        <Badge size="sm" color="green" variant="filled">
                          RESOLVED
                        </Badge>
                      </Group>
                      <Text size="sm" fw={500}>
                        {error.discrepancy?.error || error.errorType?.error
                          ? getErrorDisplayName(
                              error.discrepancy?.error ||
                                error.errorType?.error ||
                                ""
                            )
                          : error.description || "Unknown Error"}
                      </Text>
                      <Text size="xs" c="dimmed">
                        Resolved by {error.resolvedBy?.fullName || "Unknown"} •{" "}
                        {formatDate(error.resolvedAt || "")}
                      </Text>
                    </Stack>
                  </Card>
                ))}
              </Stack>
            )}
          </Stack>
        )}
      </Stack>

      <ErrorReportModal
        opened={errorModalOpened}
        onClose={handleCloseErrorModal}
        caseId={caseId}
      />

      <Modal.Root
        opened={detailModalOpened}
        onClose={handleCloseDetailModal}
        size="xl"
        centered
        styles={{
          content: {
            display: "flex",
            flexDirection: "column",
            maxHeight: "85vh",
          },
        }}
      >
        <Modal.Overlay />
        <Modal.Content>
          <Modal.Header>
            <Modal.Title>
              <Group gap="xs">
                {selectedError?.status === "resolved" ? (
                  <IconCheck size={20} color="var(--mantine-color-green-6)" />
                ) : (
                  <IconAlertTriangle
                    size={20}
                    color="var(--mantine-color-orange-6)"
                  />
                )}
                <Text fw={600}>Error Details</Text>
                {errorRefLabel(selectedError?.refNumber) && (
                  <Badge size="sm" variant="outline" color="blue">
                    {errorRefLabel(selectedError?.refNumber)}
                  </Badge>
                )}
              </Group>
            </Modal.Title>
            <Modal.CloseButton />
          </Modal.Header>

          <Modal.Body style={{ flex: 1, overflow: "auto" }}>
            {selectedError && (
              <Stack gap="md">
                <div>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                    Error Type
                  </Text>
                  <Text size="sm" fw={500}>
                    {selectedError.discrepancy?.error ||
                    selectedError.errorType?.error
                      ? getErrorDisplayName(
                          selectedError.discrepancy?.error ||
                            selectedError.errorType?.error ||
                            ""
                        )
                      : "Unknown Error"}
                  </Text>
                  {(selectedError.discrepancy?.description ||
                    selectedError.errorType?.description) && (
                    <Text size="xs" c="dimmed">
                      {selectedError.discrepancy?.description ||
                        selectedError.errorType?.description}
                    </Text>
                  )}
                </div>

                <div>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                    Description
                  </Text>
                  <div
                    style={{
                      fontSize: "var(--mantine-font-size-sm)",
                      wordBreak: "break-word",
                    }}
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{
                      __html: sanitizeHtml(selectedError.description || ""),
                    }}
                  />
                </div>

                <Group gap="xs">
                  {selectedError.blockCase && (
                    <Badge color="red" variant="filled">
                      Blocks Case
                    </Badge>
                  )}
                  {selectedError.preventSignOff && (
                    <Badge color="orange" variant="filled">
                      Prevents Sign-Off
                    </Badge>
                  )}
                </Group>

                <Divider />

                <div>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                    Reported By
                  </Text>
                  <Text size="sm">
                    {selectedError.reportedBy?.fullName ||
                      selectedError.createdBy?.fullName ||
                      "Unknown"}{" "}
                    on{" "}
                    {formatDateTime(
                      selectedError.reportedAt || selectedError.createdAt || ""
                    )}
                  </Text>
                </div>

                {selectedError.status === "resolved" && (
                  <>
                    <Divider />
                    <div>
                      <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                        Resolved By
                      </Text>
                      <Text size="sm">
                        {selectedError.resolvedBy?.fullName || "Unknown"} on{" "}
                        {formatDateTime(selectedError.resolvedAt || "")}
                      </Text>
                    </div>
                    {selectedError.resolutionNotes && (
                      <div>
                        <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                          Resolution Notes
                        </Text>
                        <div
                          style={{
                            fontSize: "var(--mantine-font-size-sm)",
                            wordBreak: "break-word",
                          }}
                          // eslint-disable-next-line react/no-danger
                          dangerouslySetInnerHTML={{
                            __html: sanitizeHtml(
                              selectedError.resolutionNotes || ""
                            ),
                          }}
                        />
                      </div>
                    )}
                    {selectedError.correctionRequestId && (
                      <div>
                        <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                          Linked Correction
                        </Text>
                        <Button
                          variant="subtle"
                          size="xs"
                          mt={4}
                          onClick={handleNavigateToCorrections}
                        >
                          {selectedError.correctionRequest?.refNumber != null
                            ? `View Linked Correction C${selectedError.correctionRequest.refNumber}`
                            : "View Linked Correction"}
                        </Button>
                      </div>
                    )}
                  </>
                )}

                {selectedError.status === "pending" && (
                  <>
                    <Divider />
                    <div>
                      <Text
                        size="xs"
                        c="dimmed"
                        tt="uppercase"
                        fw={600}
                        mb="xs"
                      >
                        Resolution Notes
                      </Text>
                      <RichTextDiagnosis
                        value={resolutionNotes}
                        onChange={setResolutionNotes}
                        placeholder="Enter resolution notes..."
                      />
                    </div>
                    <div>
                      <Text
                        size="xs"
                        c="dimmed"
                        tt="uppercase"
                        fw={600}
                        mb="xs"
                      >
                        Link Correction (Optional)
                      </Text>
                      <Select
                        placeholder={
                          correctionOptions.length === 0
                            ? "No pending corrections for this case"
                            : "Search corrections for this case..."
                        }
                        searchable
                        clearable
                        data={correctionOptions}
                        value={linkedCorrectionId}
                        onChange={setLinkedCorrectionId}
                        disabled={correctionOptions.length === 0}
                        nothingFoundMessage="No results"
                      />
                    </div>
                  </>
                )}
              </Stack>
            )}
          </Modal.Body>

          <Divider />
          <Group justify="space-between" p="md">
            <Button variant="default" onClick={handleCloseDetailModal}>
              {selectedError?.status === "resolved" ? "Close" : "Cancel"}
            </Button>
            {selectedError?.status === "pending" && (
              <Button
                color="green"
                onClick={handleResolve}
                loading={resolveMutation.isPending}
              >
                Mark as Resolved
              </Button>
            )}
          </Group>
        </Modal.Content>
      </Modal.Root>
    </>
  );
}
